import BaseService from '@/lib/BaseService'
import { NavigationTree } from '@/types/mms'
import store from '@/store'

// Original API Navigation Item interface
interface ApiNavigationItem {
    key: string
    MMSID: number
    MMSParentId?: number
    path: string
    title: string
    icon?: string
    enabled: boolean
    visible?: boolean
    order: number
    subMenu?: ApiNavigationItem[]
}

const NavigationService = {
    /**
     * Fetches the navigation tree from the backend API
     * @returns Promise with navigation tree data
     */
    getNavigationTree: async (
        signal?: AbortSignal,
    ): Promise<NavigationTree[]> => {
        // Get languageId and baId from Redux store
        const { languageId, baId } = store.getState().auth.user

        // Build query parameters
        const params = new URLSearchParams()
        if (languageId) params.append('languageId', languageId.toString())
        if (baId) params.append('baId', baId.toString())

        // Make API request with query parameters
        const response = await BaseService.get(`/api/screen/mms`, {
            params,
            signal,
        })

        // Transform the API response to match the NavigationTree interface
        // This handles both object and string responses
        const transformedData = transformNavigationData(response.data)
        return transformedData
    },

    /**
     * Fetches the navigation tree from the backend API with specific parameters
     * @param languageId - The language ID to use for the navigation
     * @param baId - The business administration ID to use for the navigation
     * @returns Promise with navigation tree data
     */
    getNavigationTreeWithParams: async (
        languageId?: number,
        baId?: number,
        signal?: AbortSignal,
    ): Promise<NavigationTree[]> => {
        // Build query parameters
        const params = new URLSearchParams()
        if (languageId) params.append('languageId', languageId.toString())
        if (baId) params.append('baId', baId.toString())

        // Make API request with query parameters
        const response = await BaseService.get(`/api/screen/mms`, {
            params,
            signal,
        })

        // Transform the API response to match the NavigationTree interface
        // This handles both object and string responses
        const transformedData = transformNavigationData(response.data)
        return transformedData
    },

    /**
     * Transforms raw navigation string data to NavigationTree format
     * @param navigationData - The navigation data string to transform
     * @returns Transformed navigation tree data
     */
    transformNavigationString: (navigationData: string): NavigationTree[] => {
        return transformNavigationData(navigationData)
    },
}

/**
 * Transform API navigation data to match the NavigationTree interface
 * @param apiData The navigation data from the API
 * @returns Transformed navigation data compatible with NavigationTree
 */
const transformNavigationData = (
    apiData: ApiNavigationItem[] | string,
): NavigationTree[] => {
    // If the API data is a string, parse it into an object
    let parsedData: ApiNavigationItem[]

    if (typeof apiData === 'string') {
        try {
            parsedData = JSON.parse(apiData)
        } catch (error) {
            console.error('Failed to parse navigation data:', error)
            return []
        }
    } else {
        parsedData = apiData
    }

    // Determine which format the data is in
    const isMMSFormat = parsedData.length > 0 && 'MMSID' in parsedData[0]

    if (isMMSFormat) {
        // Handle MMS format
        return transformMMSData(parsedData as ApiNavigationItem[])
    } else {
        return []
    }
}

/**
 * Transform the MMS format from the updated API
 */
const transformMMSData = (mmsData: ApiNavigationItem[]): NavigationTree[] => {
    const transformItem = (item: ApiNavigationItem): NavigationTree => {
        // Determine the navigation type based on the presence of subMenu
        const type =
            item.subMenu && item.subMenu.length > 0
                ? 'collapse' // If it has subMenu, it's a collapse type
                : 'item' // Otherwise it's an item type

        // Generate a path from the key if not explicitly provided
        // This is a placeholder implementation - you might need to adjust this
        // based on your routing requirements
        //const path = `/${item.key.toLowerCase().replace(/_/g, '/')}`

        return {
            key: item.key,
            path: item.path || '',
            title: item.title,
            MMSID: item.MMSID,
            icon: item.icon || '',
            type: type,
            enabled: item.enabled,
            visible: item.visible || false,
            subMenu: item.subMenu ? item.subMenu.map(transformItem) : [],
        }
    }

    return mmsData.map(transformItem)
}

export default NavigationService
